import React, { useEffect, useState } from "react";
import { useAuth, useColor, useUser, useSidebar, useTheme } from "../../common/hooks";
import { themeType } from "../../common/constants/theme-type";
import { layoutStyle } from "../layout-ui/style";
import { MuiColorInput } from "mui-color-input";
import { Box, Button, Grid, FormControlLabel, Switch } from "@mui/material";

export const Profile = () => {
  const { colorHeader, setColorHeader, colorSidebar, setColorSidebar, colorFooter, setColorFooter, colorBg, setColorBg } = useColor();
  const { currentThemeType, changeTheme } = useTheme();
  const isDark = currentThemeType === themeType.dark;
  const classes = layoutStyle();
  const { onLogout } = useAuth();
  const { isSidebarOpen } = useSidebar();
  const { userData, getUserById } = useUser();
  const [location, setLocation] = useState();

  const userName = localStorage.getItem("name") || "User";
  const userType = localStorage.getItem("type") || "N/A";
  const typeId = localStorage.getItem("typeId") || "N/A";

  const handleChange = (newValue) => {
    setColorHeader(newValue);
  };

  useEffect(() => {
    const userId = localStorage.getItem("cashflow_admin_user_id");
    if (userId && !userData) {
      getUserById(userId);
    }
    navigator.geolocation.getCurrentPosition(function (location) {
      setLocation(location.coords);
    });
  }, []);

  const handleLogout = async () => {
    await onLogout();
  };

  return (
    <div style={{ ...styles.wrapper, marginLeft: isSidebarOpen ? 280 : 70 }}>
      {/* Ambient blobs */}
      <div style={styles.blob1} />
      <div style={styles.blob2} />
      <div style={styles.dots} />

      {/* Split layout container */}
      <div style={styles.splitContainer}>

        {/* Left Column: Profile Info */}
        <div style={{
          ...styles.card,
          background: isDark ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
          border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(148, 163, 184, 0.25)",
          boxShadow: isDark ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)" : "0 20px 45px rgba(15, 23, 42, 0.08)",
        }}>
          <div style={styles.headerArea}>
            <div style={styles.avatarBox}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none"
                stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            </div>
            <div style={styles.headerText}>
              <h1 style={{ ...styles.greeting, color: isDark ? "#F8FAFC" : "#0F172A" }}>{userName}</h1>
              <p style={{ ...styles.sub, color: isDark ? "#94A3B8" : "#475569" }}>{userType.charAt(0).toUpperCase() + userType.slice(1)} Account</p>
            </div>
          </div>

          <div style={{
            ...styles.divider,
            background: isDark ? "rgba(255,255,255,.08)" : "rgba(148, 163, 184, 0.25)",
          }} />

          <div style={styles.detailsGrid}>
            <div style={styles.detailBox}>
              <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Account Name</span>
              <span style={{ ...styles.detailValue, color: isDark ? "#F1F5F9" : "#0F172A" }}>{userName}</span>
            </div>
            <div style={styles.detailBox}>
              <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Role</span>
              <span style={{ ...styles.detailValue, textTransform: 'capitalize', color: isDark ? "#F1F5F9" : "#0F172A" }}>{userType}</span>
            </div>
            {userData?.email && (
              <div style={styles.detailBox}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Email</span>
                <span style={{ ...styles.detailValue, color: isDark ? "#F1F5F9" : "#0F172A" }}>{userData?.email}</span>
              </div>
            )}
            {location && (
              <div style={styles.detailBox}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Location</span>
                <span style={{ ...styles.detailValue, color: isDark ? "#F1F5F9" : "#0F172A" }}>
                  {location.latitude.toFixed(4)}, {location.longitude.toFixed(4)}
                </span>
              </div>
            )}
          </div>

          <div style={{ flexGrow: 1 }} />
          <div style={{
            ...styles.divider,
            background: isDark ? "rgba(255,255,255,.08)" : "rgba(148, 163, 184, 0.25)",
          }} />

          <div style={styles.actionArea}>
            <Button
              onClick={handleLogout}
              sx={{
                width: "100%",
                padding: "12px 24px",
                borderRadius: "12px",
                border: "1px solid rgba(239,68,68,.3)",
                background: "rgba(239,68,68,.1)",
                color: "#FCA5A5",
                fontSize: 14,
                fontWeight: 600,
                textTransform: "none",
                "&:hover": {
                  background: "rgba(239,68,68,.2)",
                  borderColor: "rgba(239,68,68,.5)",
                }
              }}
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Right Column: Preferences */}
        <div style={{
          ...styles.card,
          background: isDark ? "rgba(30, 41, 59, 0.4)" : "rgba(255, 255, 255, 0.8)",
          border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(148, 163, 184, 0.25)",
          boxShadow: isDark ? "0 25px 50px -12px rgba(0, 0, 0, 0.5)" : "0 20px 45px rgba(15, 23, 42, 0.08)",
        }}>
          <div style={styles.settingsArea}>
            <h3 style={{ ...styles.sectionTitle, color: isDark ? "#F8FAFC" : "#0F172A" }}>Global Preferences</h3>
            <p style={{ ...styles.sub, color: isDark ? "#94A3B8" : "#475569" }}>Customize your application theme colors below.</p>

            <div style={{ marginTop: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{
                ...styles.colorPickerRow,
                background: isDark ? "rgba(255,255,255,.03)" : "rgba(148, 163, 184, 0.06)",
                border: isDark ? "1px solid rgba(255,255,255,.05)" : "1px solid rgba(148, 163, 184, 0.2)",
              }}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Header Color</span>
                <MuiColorInput
                  format="hex"
                  value={colorHeader || "#020617"}
                  onChange={setColorHeader}
                  sx={{
                    ...styles.colorInputProps,
                    ".MuiInputBase-root": {
                      background: isDark ? "rgba(255,255,255,.05)" : "#fff",
                      color: isDark ? "#F8FAFC" : "#0F172A",
                      borderRadius: "12px",
                      height: 40,
                      border: isDark ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(148, 163, 184, 0.3)",
                    },
                  }}
                />
              </div>
              <div style={{
                ...styles.colorPickerRow,
                background: isDark ? "rgba(255,255,255,.03)" : "rgba(148, 163, 184, 0.06)",
                border: isDark ? "1px solid rgba(255,255,255,.05)" : "1px solid rgba(148, 163, 184, 0.2)",
              }}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Sidebar Color</span>
                <MuiColorInput
                  format="hex"
                  value={colorSidebar || "#020617"}
                  onChange={setColorSidebar}
                  sx={{
                    ...styles.colorInputProps,
                    ".MuiInputBase-root": {
                      background: isDark ? "rgba(255,255,255,.05)" : "#fff",
                      color: isDark ? "#F8FAFC" : "#0F172A",
                      borderRadius: "12px",
                      height: 40,
                      border: isDark ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(148, 163, 184, 0.3)",
                    },
                  }}
                />
              </div>
              <div style={{
                ...styles.colorPickerRow,
                background: isDark ? "rgba(255,255,255,.03)" : "rgba(148, 163, 184, 0.06)",
                border: isDark ? "1px solid rgba(255,255,255,.05)" : "1px solid rgba(148, 163, 184, 0.2)",
              }}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Footer Color</span>
                <MuiColorInput
                  format="hex"
                  value={colorFooter || "#020617"}
                  onChange={setColorFooter}
                  sx={{
                    ...styles.colorInputProps,
                    ".MuiInputBase-root": {
                      background: isDark ? "rgba(255,255,255,.05)" : "#fff",
                      color: isDark ? "#F8FAFC" : "#0F172A",
                      borderRadius: "12px",
                      height: 40,
                      border: isDark ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(148, 163, 184, 0.3)",
                    },
                  }}
                />
              </div>
              <div style={{
                ...styles.colorPickerRow,
                background: isDark ? "rgba(255,255,255,.03)" : "rgba(148, 163, 184, 0.06)",
                border: isDark ? "1px solid rgba(255,255,255,.05)" : "1px solid rgba(148, 163, 184, 0.2)",
              }}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Background Color</span>
                <MuiColorInput
                  format="hex"
                  value={colorBg || "#020617"}
                  onChange={setColorBg}
                  sx={{
                    ...styles.colorInputProps,
                    ".MuiInputBase-root": {
                      background: isDark ? "rgba(255,255,255,.05)" : "#fff",
                      color: isDark ? "#F8FAFC" : "#0F172A",
                      borderRadius: "12px",
                      height: 40,
                      border: isDark ? "1px solid rgba(255,255,255,.1)" : "1px solid rgba(148, 163, 184, 0.3)",
                    },
                  }}
                />
              </div>
              <div style={{
                ...styles.colorPickerRow,
                background: isDark ? "rgba(255,255,255,.03)" : "rgba(148, 163, 184, 0.06)",
                border: isDark ? "1px solid rgba(255,255,255,.05)" : "1px solid rgba(148, 163, 184, 0.2)",
              }}>
                <span style={{ ...styles.detailLabel, color: isDark ? "#94A3B8" : "#475569" }}>Theme Mode</span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentThemeType === themeType.dark}
                      onChange={() =>
                        changeTheme(
                          currentThemeType === themeType.dark
                            ? themeType.light
                            : themeType.dark,
                        )
                      }
                      color="primary"
                    />
                  }
                  label={
                    currentThemeType === themeType.dark ? "Dark Mode" : "Light Mode"
                  }
                  labelPlacement="start"
                  sx={{
                    color: isDark ? "#F8FAFC" : "#0F172A",
                    ".MuiTypography-root": {
                      color: isDark ? "#F8FAFC" : "#0F172A",
                    },
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles = {
  wrapper: {
    minHeight: "calc(100vh - 120px)",
    marginTop: 60,
    transition: "margin-left 0.3s ease",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
    overflow: "hidden",
    fontFamily: "'Outfit', 'Inter', 'Roboto', 'Helvetica', sans-serif",
    padding: "20px"
  },
  blob1: {
    position: "absolute",
    width: 500,
    height: 500,
    borderRadius: "50%",
    background: "rgba(14,165,233,.15)",
    filter: "blur(150px)",
    top: -100,
    left: -100,
    pointerEvents: "none",
  },
  blob2: {
    position: "absolute",
    width: 500,
    height: 500,
    borderRadius: "50%",
    background: "rgba(139,92,246,.15)",
    filter: "blur(160px)",
    bottom: -100,
    right: -100,
    pointerEvents: "none",
  },
  dots: {
    position: "absolute",
    inset: 0,
    backgroundImage: "radial-gradient(rgba(56,189,248,.07) 1px, transparent 1px)",
    backgroundSize: "38px 38px",
    pointerEvents: "none",
  },
  splitContainer: {
    display: "flex",
    flexDirection: "row",
    gap: "24px",
    width: "100%",
    maxWidth: "1000px",
    zIndex: 1,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  card: {
    flex: "1 1 400px",
    background: "rgba(30, 41, 59, 0.4)",
    backdropFilter: "blur(20px)",
    borderRadius: "24px",
    border: "1px solid rgba(255,255,255,0.05)",
    padding: "32px",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.5)",
    display: "flex",
    flexDirection: "column",
  },
  headerArea: {
    display: "flex",
    alignItems: "center",
    gap: 24,
    marginBottom: 32,
  },
  avatarBox: {
    width: 80,
    height: 80,
    borderRadius: 24,
    background: "linear-gradient(135deg,#1D4ED8,#7C3AED)",
    boxShadow: "0 0 36px rgba(37,99,235,.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1.5px solid rgba(255,255,255,.15)",
    flexShrink: 0,
  },
  headerText: {
    display: "flex",
    flexDirection: "column",
  },
  greeting: {
    color: "#F8FAFC",
    fontSize: 28,
    fontWeight: 800,
    margin: "0 0 6px",
    letterSpacing: -0.5,
  },
  sub: {
    color: "#94A3B8",
    fontSize: 15,
    margin: 0,
    fontWeight: 500,
  },
  divider: {
    height: 1,
    background: "rgba(255,255,255,.08)",
    margin: "32px 0",
  },
  detailsGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 24,
  },
  detailBox: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  detailLabel: {
    color: "#94A3B8",
    fontSize: 13,
    fontWeight: 600,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  detailValue: {
    color: "#F1F5F9",
    fontSize: 16,
    fontWeight: 500,
  },
  settingsArea: {
    display: "flex",
    flexDirection: "column",
    gap: 16,
  },
  sectionTitle: {
    color: "#F8FAFC",
    fontSize: 18,
    fontWeight: 700,
    margin: 0,
  },
  colorPickerRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px",
    background: "rgba(255,255,255,.03)",
    borderRadius: "16px",
    border: "1px solid rgba(255,255,255,.05)",
  },
  colorInputProps: {
    width: 200,
    ".MuiInputBase-root": {
      background: "rgba(255,255,255,.05)",
      color: "#F8FAFC",
      borderRadius: "12px",
      height: 40,
      border: "1px solid rgba(255,255,255,.1)",
    },
    ".MuiButtonBase-root": {
      padding: "4px",
    }
  },
  actionArea: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: 32,
  }
};
